import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  getAthleteAvailableEvents,
  updateAthleteEvents,
  registerVisitorForMeet,
  getRepresentingClubs,
  searchClubAthletesForMeet,
} from 'api/meetRegistrationApi';
import { findAthleteMatches, getSuggestedClubAthleteNumber } from 'api/userApi';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import CreatableClubSelect, { UNATTACHED } from './CreatableClubSelect';

// Confidence label for a possible-duplicate-athlete match (mirrors AthleteMatchingService tiers).
const MATCH_TIER = {
  Exact: { label: 'Exact match', color: '#b91c1c' },
  LikelyFuzzyName: { label: 'Likely match', color: '#c2410c' },
  LikelyDobTypo: { label: 'Likely (DOB typo)', color: '#c2410c' },
  SameNameVerify: { label: 'Same name', color: '#6b7280' },
  SimilarNameVerify: { label: 'Similar name', color: '#c2410c' },
};

// A complete-looking email (the same check as the public sign-up's RegisterDialog). The duplicate check sends the
// contact email only once it looks complete, so typing it doesn't send a request per keystroke.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Format a competing age group — just the plain competing age (no "U" prefix), the same number admins use to identify
// an athlete's group. Single source of truth for the age-group label across this dialog.
const formatAgeGroup = (age) => (!age || age <= 0 ? '' : String(age));

// Membership type → display label for roster rows ("Full" reads as "Member").
const membershipLabel = (type) => (type === 'Full' ? 'Member' : type || 'Athlete');

// Drop a trailing " Club" word for the registration fields ("SplitFast Athletics Club" → "SplitFast Athletics"). The
// found-athlete panel keeps the full name; this only cleans values that feed the representing-club field.
const stripClubSuffix = (name) => {
  if (!name) return name;
  const stripped = name.replace(/\s+club\s*$/i, '').trim();
  return stripped || name;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Format an ISO date (yyyy-MM-dd) as "7 Feb 2013" — a spelled-out month removes the day/month order ambiguity that
// makes a transposed DOB (a very common typo) look identical to the original. Parsed by parts to avoid timezone shifts.
const formatDob = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-');
  const mi = parseInt(m, 10) - 1;
  if (!y || !d || mi < 0 || mi > 11) return iso;
  return `${parseInt(d, 10)} ${MONTHS[mi]} ${y}`;
};

// Manual meet-registration (§6). ADD is ONE "add an athlete to this meet" form (mirrors the Add-Athlete-to-club
// modal): enter the athlete; as the identity fills in, existing athletes surface so you don't create a duplicate —
// each can be used directly. One Save covers every case:
//   • a match already in this club  → register as-is (existing membership, no changes)
//   • a match outside this club, in the family whose contact email was entered → add as a visitor (links them in)
//   • no match                       → create a new athlete (as a visitor)
// EDIT just changes an existing registration's events.
const AthleteEventsEditPopup = ({
  isOpen = true,
  onClose = () => {},
  athlete = null,
  meetId = null,
  clubId = null,
  onSave = () => {},
  mode = 'edit',
  isAdmin = false, // editing a guest/visitor's identity is Admin-only; Officials get events-only
}) => {
  // identity + contact + per-club fields (create-new)
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactFirst, setContactFirst] = useState('');
  const [contactLast, setContactLast] = useState('');
  const [clubNumber, setClubNumber] = useState('');
  // Representing club. '' = nothing chosen yet, UNATTACHED = no club, else a club name. (Replaces the old free text.)
  const [externalClub, setExternalClub] = useState('');
  const [numberHint, setNumberHint] = useState('');

  // Representing-club picker data: distinct clubs already in this meet + the host club name (to lock full members).
  const [clubsList, setClubsList] = useState([]);
  const [hostClubName, setHostClubName] = useState('');

  // Add view has two sub-modes: 'search' (find an existing club athlete by name/bib — the common case) and 'new'
  // (enter a brand-new athlete; runs platform-wide duplicate detection). A selected athlete (`linked`) supersedes both.
  const [addView, setAddView] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [rosterResults, setRosterResults] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);

  // duplicate detection (new-athlete path) + chosen existing athlete
  const [matches, setMatches] = useState([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [linked, setLinked] = useState(null); // a chosen match { athleteProfileID, firstName, ..., isInThisClub }

  // events
  const [availableEvents, setAvailableEvents] = useState([]);
  const [maxEvents, setMaxEvents] = useState(null);
  const [ageGroup, setAgeGroup] = useState(null); // server-computed competing age group label, e.g. "U11"
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState({});
  const [originalEvents, setOriginalEvents] = useState({});

  const [isSaving, setIsSaving] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);

  const registerAsIs = !!(linked && linked.isInThisClub); // in this club → register existing membership as-is
  const addAsVisitor = !!(linked && !linked.isInThisClub); // on the platform elsewhere → link as visitor
  // An unselected exact match (same name + DOB + gender) is the same athlete — creating a new record would be a
  // duplicate, so the admin must pick it. Fuzzy matches stay advisory (admin can still create new).
  const hasExactMatch = !linked && matches.some((m) => m.matchTier === 'Exact');
  // An exact match who's already a member of THIS club is named as such ("{Club} member"), not "already on SplitFast".
  const exactMatchInClub =
    !linked && matches.some((m) => m.matchTier === 'Exact' && m.isInThisClub);

  // Editing a guest/visitor registration → name/DOB/gender/bib/club are all editable (Admin only; full members and
  // non-admins get events only).
  const editGuest =
    mode === 'edit' && !!athlete && athlete.memberType !== 'Full' && isAdmin;

  // The add-flow shows the detail fields once a specific athlete is in play (selected, or the new-athlete form open).
  const addHasAthlete = mode === 'add' && (!!linked || addView === 'new');
  // Events show for any edit (members included) and for the add flow once an athlete is in play.
  const showEvents = mode === 'edit' || addHasAthlete;

  // Representing club: locked to the host club for a full member of this club; active (must choose) for everyone else
  // (guests/visitors already in this club, visitors from elsewhere, brand-new athletes).
  const lockedToHostClub = registerAsIs && linked?.inClubMembershipType === 'Full';
  // The bib/club row shows when editing a guest/visitor, or in the add flow (but not while an exact match is unpicked).
  const clubFieldShown = editGuest || (addHasAthlete && !hasExactMatch);
  const clubFieldActive = clubFieldShown && !lockedToHostClub;

  // Reset on close.
  useEffect(() => {
    if (!isOpen) {
      setFirst('');
      setLast('');
      setBirthDate('');
      setGender('');
      setContactEmail('');
      setContactFirst('');
      setContactLast('');
      setClubNumber('');
      setExternalClub('');
      setNumberHint('');
      setMatches([]);
      setLinked(null);
      setAvailableEvents([]);
      setMaxEvents(null);
      setSelectedEvents({});
      setOriginalEvents({});
      setAddView('search');
      setSearchQuery('');
      setRosterResults([]);
    }
  }, [isOpen]);

  // Load the representing-club picker data (host club name + clubs already in this meet) — needed by the Add flow and
  // the guest/visitor edit form (both expose the representing-club picker).
  useEffect(() => {
    if (!isOpen || !meetId || !(mode === 'add' || editGuest)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await getRepresentingClubs(meetId);
        if (cancelled) return;
        setClubsList(res.data?.clubs || []);
        setHostClubName(res.data?.hostClubName || '');
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load representing clubs:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, editGuest, meetId]);

  // Roster search (search view): debounced lookup of this club's athletes by name/bib. Empty query returns the roster.
  useEffect(() => {
    if (!isOpen || mode !== 'add' || addView !== 'search' || linked || !meetId)
      return undefined;
    const timer = setTimeout(async () => {
      setRosterLoading(true);
      try {
        const res = await searchClubAthletesForMeet(meetId, searchQuery.trim());
        setRosterResults(res.data?.athletes || []);
      } catch (err) {
        console.error('Roster search failed:', err);
        setRosterResults([]);
      } finally {
        setRosterLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [isOpen, mode, addView, linked, meetId, searchQuery]);

  // Edit mode: seed selected events from the current registration, and (for guests/visitors) the editable identity.
  // Keyed on isOpen too, so re-opening the same athlete re-seeds (the popup stays mounted between opens).
  useEffect(() => {
    if (!isOpen || mode !== 'edit' || !athlete) return;
    const obj = (athlete.eventRegistrations || []).reduce((acc, e) => {
      acc[e.eventCode.toString()] = true;
      return acc;
    }, {});
    setSelectedEvents(obj);
    setOriginalEvents(obj);
    if (athlete.memberType !== 'Full') {
      setFirst(athlete.firstName || '');
      setLast(athlete.lastName || '');
      setBirthDate(athlete.birthDate || '');
      setGender(athlete.gender || '');
      setClubNumber(athlete.clubAthleteNumber ? String(athlete.clubAthleteNumber) : '');
      // Their current representing club, or the "unattached" sentinel when they have none.
      setExternalClub(athlete.clubName ? athlete.clubName : UNATTACHED);
    }
  }, [isOpen, mode, athlete]);

  // The contact email the duplicate check sends: only once it looks complete.
  const familyEmail = EMAIL_RE.test(contactEmail.trim()) ? contactEmail.trim() : '';

  // Duplicate detection (new-athlete view): once name + gender are present (and we haven't picked someone), ask the
  // server for possible matches. Debounced. The server returns this club's athletes, and an athlete outside the club
  // only when the contact email entered is that athlete's primary contact email (its own family); never another
  // club's athlete (Doug, 2026-11-05).
  useEffect(() => {
    if (mode !== 'add' || addView !== 'new' || linked || !clubId) return undefined;
    // Search on name + gender (DOB optional). A DOB, once entered, refines matches to exact / DOB-typo tiers.
    if (!first.trim() || !last.trim() || !gender) {
      setMatches([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setMatchLoading(true);
      try {
        const res = await findAthleteMatches(clubId, {
          firstName: first.trim(),
          lastName: last.trim(),
          gender,
          ...(birthDate ? { birthDate } : {}),
          ...(familyEmail ? { contactEmail: familyEmail } : {}),
          includeContactEmail: true, // admin meet-reg flow — show the contact to identify/register the athlete
        });
        setMatches(res.data || []);
      } catch (err) {
        console.error('Athlete match lookup failed:', err);
        setMatches([]);
      } finally {
        setMatchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [mode, addView, linked, first, last, gender, birthDate, familyEmail, clubId]);

  // What identity drives the available events + cap.
  const eventQuery = useMemo(() => {
    if (mode === 'edit' && athlete) {
      // Guest/visitor edit: follow the edited DOB+gender live (age group can change). Member: their fixed age group.
      if (editGuest && birthDate && gender) {
        return { birthDate, gender };
      }
      return { age: athlete.ageGroup, gender: athlete.gender };
    }
    if (linked) {
      return { birthDate: linked.birthDate, gender: linked.gender };
    }
    if (birthDate && gender) {
      return { birthDate, gender };
    }
    return null;
  }, [mode, athlete, editGuest, linked, birthDate, gender]);

  useEffect(() => {
    if (!isOpen || !eventQuery || !eventQuery.gender) return undefined;
    if (!eventQuery.age && !eventQuery.birthDate) return undefined;
    let cancelled = false;
    setEventsLoading(true);
    (async () => {
      try {
        const res = await getAthleteAvailableEvents(meetId, eventQuery);
        if (cancelled) return;
        setAvailableEvents(res.data?.events || []);
        setMaxEvents(res.data?.maxEvents ?? null);
        setAgeGroup(res.data?.ageGroup || null);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load available events:', err);
        setAvailableEvents([]);
        setMaxEvents(null);
        setAgeGroup(null);
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventQuery, meetId]);

  const selectedCount = Object.values(selectedEvents).filter(Boolean).length;
  const capReached = maxEvents != null && selectedCount >= maxEvents;

  const toggleEvent = (code) => {
    setSelectedEvents((prev) => {
      const on = !!prev[code];
      if (!on && capReached) return prev;
      return { ...prev, [code]: !on };
    });
  };

  const chooseMatch = async (m) => {
    setLinked(m);
    setMatches([]);
    setSelectedEvents({});
    setClubNumber('');
    setNumberHint('');
    if (m.isInThisClub) {
      // In this club already. A full member is locked to the host club (no value needed); a guest/visitor pre-fills
      // their current representing club (editable) — or stays unchosen if they never had one.
      setExternalClub(
        m.inClubMembershipType === 'Full'
          ? ''
          : stripClubSuffix(m.inClubExternalClubName || '') || ''
      );
      return; // registers as-is, no new number
    }
    // An athlete from outside this club (found through their family's email). The server never says which other club
    // they are in or their bib there (Doug, 2026-11-05), so the admin chooses the representing club and enters a bib,
    // unless they raced here before.
    setExternalClub('');
    try {
      const res = await getSuggestedClubAthleteNumber(clubId, m.athleteProfileID);
      const { suggestedNumber, reason } = res.data || {};
      if (suggestedNumber) {
        // They've raced here before — reuse that number.
        setClubNumber(String(suggestedNumber));
        setNumberHint(`Previously raced here as #${suggestedNumber}`);
      } else if (reason === 'previous-number-in-use') {
        setNumberHint('Their previous number is in use — enter a new one.');
      }
    } catch {
      // No suggestion: the admin enters a bib.
    }
  };

  // Select an athlete from the roster search — they're always in this club, so this is the register-as-is path.
  const chooseRosterAthlete = (a) => {
    chooseMatch({
      athleteProfileID: a.athleteID,
      firstName: a.firstName,
      lastName: a.lastName,
      birthDate: a.birthDate,
      gender: a.gender,
      ageGroup: a.ageGroup,
      isInThisClub: true,
      inClubMembershipType: a.membershipType,
      inClubExternalClubName: a.externalClubName || '',
      inClubAthleteNumber: a.clubAthleteNumber,
      clubs: [],
    });
  };

  // Switch to the new-athlete form, carrying over whatever name was typed in the search box.
  const goToNewAthlete = () => {
    const parts = searchQuery.trim().split(/\s+/);
    if (parts[0]) {
      setFirst(parts[0]);
      setLast(parts.slice(1).join(' '));
    }
    setAddView('new');
  };

  // Clear all new-athlete entry and return to the search view.
  const backToSearch = () => {
    setFirst('');
    setLast('');
    setBirthDate('');
    setGender('');
    setContactEmail('');
    setContactFirst('');
    setContactLast('');
    setClubNumber('');
    setExternalClub('');
    setNumberHint('');
    setMatches([]);
    setSelectedEvents({});
    setAddView('search');
  };

  const clearLinked = () => {
    setLinked(null);
    setClubNumber('');
    setNumberHint('');
    setSelectedEvents({});
    setExternalClub('');
    setAddView('search');
  };

  // Add a club to the in-session option list (deduped, sorted) so the next athlete sees it.
  const addClubOption = (club) => {
    setClubsList((prev) =>
      prev.some((c) => c.toLowerCase() === club.toLowerCase())
        ? prev
        : [...prev, club].sort((a, b) => a.localeCompare(b))
    );
  };

  // Pick / create a representing club. The "Club" suffix is dropped (registration fields omit it) and a newly-typed
  // club is added to the option list for the next athlete.
  const handleClubChange = (v) => {
    const cleaned = v === UNATTACHED ? v : stripClubSuffix(v);
    setExternalClub(cleaned);
    if (cleaned && cleaned !== UNATTACHED) addClubOption(cleaned);
  };

  // Map the picker value to the saved externalClubName: UNATTACHED / '' → null, else the trimmed club name.
  const externalClubName =
    externalClub === UNATTACHED ? null : externalClub.trim() || null;

  const eventCodes = Object.entries(selectedEvents)
    .filter(([, on]) => on)
    .map(([code]) => parseInt(code, 10));
  const numberValid = /^\d+$/.test(clubNumber) && parseInt(clubNumber, 10) > 0;

  const eventsChanged =
    JSON.stringify(selectedEvents) !== JSON.stringify(originalEvents);
  // For a guest/visitor edit, has any identity field been changed from what was loaded?
  const editIdentityChanged =
    editGuest &&
    (first.trim() !== (athlete.firstName || '') ||
      last.trim() !== (athlete.lastName || '') ||
      birthDate !== (athlete.birthDate || '') ||
      gender !== (athlete.gender || '') ||
      parseInt(clubNumber, 10) !== (athlete.clubAthleteNumber || 0) ||
      externalClub !== (athlete.clubName ? athlete.clubName : UNATTACHED));

  const canSave = (() => {
    if (mode === 'edit') {
      if (!editGuest) return eventsChanged; // member: events only
      // Guest/visitor: the form must be valid (club required), and something must have changed.
      if (!first.trim() || !last.trim() || !birthDate || !gender || !numberValid)
        return false;
      if (externalClub === '') return false;
      return editIdentityChanged || eventsChanged;
    }
    if (eventCodes.length === 0) return false;
    if (hasExactMatch) return false; // unselected exact match → must pick it, can't create a duplicate
    if (clubFieldActive && externalClub === '') return false; // representing club is mandatory where editable
    if (registerAsIs) return true; // existing club athlete — events (+ club above) only
    if (!numberValid) return false; // create-new and add-as-visitor both need a number
    if (addAsVisitor) return true;
    return first.trim() && last.trim() && birthDate && gender && contactEmail.trim();
  })();

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (mode === 'edit') {
        await updateAthleteEvents(
          meetId,
          athlete.athleteID,
          eventCodes,
          editGuest
            ? {
                editIdentity: true,
                firstName: first.trim(),
                lastName: last.trim(),
                birthDate,
                gender,
                clubAthleteNumber: parseInt(clubNumber, 10),
                externalClubName,
              }
            : {}
        );
      } else if (registerAsIs) {
        // Already in this club. Full member → register as-is (host club). Guest/visitor → also persist their chosen
        // representing club (changes their membership everywhere).
        await updateAthleteEvents(
          meetId,
          linked.athleteProfileID,
          eventCodes,
          lockedToHostClub
            ? {}
            : { updateRepresentingClub: true, externalClubName }
        );
      } else if (addAsVisitor) {
        // The server links an athlete from outside this club only with their family's email: the one that found them.
        await registerVisitorForMeet(meetId, {
          existingAthleteProfileId: linked.athleteProfileID,
          contactEmail: contactEmail.trim(),
          clubAthleteNumber: parseInt(clubNumber, 10),
          externalClubName,
          eventCodes,
        });
      } else {
        await registerVisitorForMeet(meetId, {
          firstName: first.trim(),
          lastName: last.trim(),
          birthDate,
          gender,
          contactEmail: contactEmail.trim(),
          contactFirstName: contactFirst.trim(),
          contactLastName: contactLast.trim(),
          clubAthleteNumber: parseInt(clubNumber, 10),
          externalClubName,
          eventCodes,
        });
      }
      toast.success(mode === 'edit' ? 'Events updated' : 'Athlete registered');
      onSave();
      onClose(true);
    } catch (err) {
      const data = err?.response?.data;
      toast.error(typeof data === 'string' ? data : 'Failed to save registration');
      console.error('Save registration failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty =
    mode === 'edit'
      ? eventsChanged || editIdentityChanged
      : !!(first || last || birthDate || gender || linked || eventCodes.length);

  const handleClose = () => {
    if (isDirty) {
      setShowPrompt(true);
      return;
    }
    onClose(false);
  };

  if (!isOpen) return null;

  const eventsName =
    mode === 'edit'
      ? editGuest
        ? `${first} ${last}`.trim()
        : `${athlete?.firstName} ${athlete?.lastName}`
      : linked
      ? `${linked.firstName} ${linked.lastName}`
      : `${first} ${last}`.trim();

  // Identity fields (First / Last / Gender / DOB) — shared by the add-new form and the guest/visitor edit form.
  const renderIdentityFields = () => (
    <div style={styles.fieldGrid}>
      <Field label="First name">
        <input
          style={styles.input}
          value={first}
          onChange={(e) => setFirst(e.target.value)}
          autoFocus
        />
      </Field>
      <Field label="Last name">
        <input
          style={styles.input}
          value={last}
          onChange={(e) => setLast(e.target.value)}
        />
      </Field>
      <Field label="Gender">
        <select
          style={styles.input}
          value={gender}
          onChange={(e) => setGender(e.target.value)}
        >
          <option value="">Select…</option>
          <option value="M">Male</option>
          <option value="F">Female</option>
        </select>
      </Field>
      <Field label="Date of birth">
        <div style={styles.dobWrap}>
          <input
            type="date"
            // Match the site's other date pickers (CustomDatePicker): appearance:none strips the native date widget so
            // the field renders as plain text (DD/MM/YYYY, not the spaced-out DD / MM / YYYY); toggle muted→dark on
            // value so a chosen date reads as normal text rather than a disabled-looking gray placeholder.
            style={{
              ...styles.input,
              width: '100%',
              boxSizing: 'border-box',
              appearance: 'none',
              WebkitAppearance: 'none',
              MozAppearance: 'none',
              color: birthDate ? '#111827' : '#9ca3af',
              textTransform: 'uppercase',
              letterSpacing: '-0.5px',
            }}
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
          />
          {birthDate ? (
            // Floats to the right of a normal-width field (the rest of the row is empty), so it adds no row height.
            <span style={styles.dobEchoSide}>{formatDob(birthDate)}</span>
          ) : null}
        </div>
      </Field>
    </div>
  );

  // Search view (default Add sub-mode): find an athlete already in this club by name/bib, or jump to the new-athlete form.
  const renderSearchView = () => (
    <>
      <Field label="Find athlete">
        <input
          style={styles.input}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name or bib number…"
          autoFocus
        />
      </Field>
      {rosterLoading && <div style={styles.meta}>Searching…</div>}
      {!rosterLoading && rosterResults.length === 0 && (
        <div style={styles.meta}>
          {searchQuery.trim()
            ? 'No athletes found in your club.'
            : 'No athletes in your club yet.'}
        </div>
      )}
      {rosterResults.length > 0 && (
        <div style={styles.matchList}>
          {rosterResults.map((a) => (
            <div key={a.athleteID} style={styles.matchRow}>
              <div style={{ minWidth: 0 }}>
                <div style={styles.matchName}>
                  {a.firstName} {a.lastName} · {a.gender}
                  {a.ageGroup ? ` · ${formatAgeGroup(a.ageGroup)}` : ''}
                </div>
                <div style={styles.matchMeta}>
                  DOB: {formatDob(a.birthDate)} · Bib #{a.clubAthleteNumber} ·{' '}
                  {membershipLabel(a.membershipType)}
                  {a.externalClubName ? ` · ${a.externalClubName}` : ''}
                </div>
              </div>
              {a.alreadyRegistered ? (
                <span style={styles.alreadyBadge}>Already registered</span>
              ) : (
                <button
                  style={styles.registerButton}
                  onClick={() => chooseRosterAthlete(a)}
                >
                  Register
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <button style={styles.addNewButton} onClick={goToNewAthlete}>
        + Add a new athlete
      </button>
    </>
  );

  const renderEvents = () => (
    <div style={styles.eventsSection}>
      <div style={styles.sectionHeader}>
        <label style={styles.sectionLabel}>
          {eventsName ? `Events for ${eventsName}` : 'Events'}
          {eventsName &&
            [formatAgeGroup(ageGroup), eventQuery?.gender].filter(Boolean)
              .length > 0 && (
              <span style={styles.sectionLabelMeta}>
                {' · '}
                {[formatAgeGroup(ageGroup), eventQuery?.gender]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            )}
        </label>
        <span style={styles.eventCount}>
          {selectedCount}
          {maxEvents != null ? ` / ${maxEvents}` : ''} selected
        </span>
      </div>
      {maxEvents != null && (
        <span style={capReached ? styles.maxNoteReached : styles.maxNote}>
          {ageGroup
            ? `${formatAgeGroup(ageGroup)}${
                eventQuery?.gender ? ` ${eventQuery.gender}` : ''
              } age group`
            : 'This age group'}{' '}
          can select a maximum of {maxEvents}{' '}
          {maxEvents === 1 ? 'event' : 'events'}.
        </span>
      )}
      {eventsLoading ? (
        <div style={styles.emptyBox}>Loading available events…</div>
      ) : availableEvents.length === 0 ? (
        <div style={styles.emptyBox}>
          {mode === 'add' && !eventQuery
            ? 'Enter date of birth and gender to see events.'
            : 'No events available for this athlete.'}
        </div>
      ) : (
        <div style={styles.eventsGrid}>
          {availableEvents.map((evt) => {
            const code = evt.eventCode.toString();
            const checked = !!selectedEvents[code];
            return (
              <label key={evt.eventCode} style={styles.eventItem}>
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={isSaving || (!checked && capReached)}
                  onChange={() => toggleEvent(code)}
                  style={styles.checkbox}
                />
                <span style={styles.eventName}>{evt.displayName}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div style={styles.modalOverlay}>
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={styles.modalTitle}>
              {mode === 'edit'
                ? editGuest
                  ? 'Edit Registration'
                  : 'Edit Athlete Events'
                : 'Add Athlete to Meet'}
            </div>
            {mode === 'add' && !linked && (
              <div style={styles.subtitle}>
                {addView === 'search'
                  ? 'Search for an athlete in your club, or add a new one.'
                  : "Enter the athlete's details — we'll flag any existing records."}
              </div>
            )}

            <div style={styles.form}>
              {mode === 'edit' ? (
                editGuest ? (
                  renderIdentityFields()
                ) : (
                  <div style={styles.summaryCard}>
                    <strong>
                      {athlete?.firstName} {athlete?.lastName}
                    </strong>
                    <span style={styles.meta}>
                      {[
                        formatAgeGroup(athlete?.ageGroup),
                        athlete?.gender,
                        athlete?.clubName,
                        athlete?.clubAthleteNumber
                          ? `#${athlete.clubAthleteNumber}`
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                )
              ) : linked ? (
                <div style={styles.summaryCard}>
                  <div style={styles.linkedHead}>
                    <strong>
                      {linked.firstName} {linked.lastName}
                    </strong>
                    <button style={styles.linkClear} onClick={clearLinked}>
                      Select a different athlete
                    </button>
                  </div>
                  <span style={styles.meta}>
                    {[
                      formatAgeGroup(ageGroup),
                      linked.gender,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {linked.primaryContactName || linked.primaryContactEmail ? (
                    <span style={styles.meta}>
                      Primary contact: {linked.primaryContactName}
                      {linked.primaryContactEmail
                        ? ` · ${linked.primaryContactEmail}`
                        : ''}
                    </span>
                  ) : null}
                  <span style={registerAsIs ? styles.inClubNote : styles.linkNote}>
                    {registerAsIs
                      ? `Already a ${membershipLabel(
                          linked?.inClubMembershipType
                        )} in your club — will be registered for this meet.`
                      : 'On SplitFast — will be added to this meet as a visitor.'}
                  </span>
                </div>
              ) : addView === 'search' ? (
                renderSearchView()
              ) : (
                <>
                  <button style={styles.backLink} onClick={backToSearch}>
                    ← Back to search
                  </button>
                  {/* Name + Gender trigger the search of your club; DOB (next row) is only needed to add a new athlete. */}
                  {renderIdentityFields()}

                  {/* The contact comes before the matches: an athlete from outside your club is found only by their
                      family's email. Hidden only while an exact match in your club must be picked. */}
                  {!exactMatchInClub && (
                    <div style={styles.fieldGrid}>
                      <Field label="Contact first name">
                        <input
                          style={styles.input}
                          value={contactFirst}
                          onChange={(e) => setContactFirst(e.target.value)}
                        />
                      </Field>
                      <Field label="Contact last name">
                        <input
                          style={styles.input}
                          value={contactLast}
                          onChange={(e) => setContactLast(e.target.value)}
                        />
                      </Field>
                      <Field label="Contact email">
                        <input
                          style={styles.input}
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                        />
                      </Field>
                    </div>
                  )}

                  {matchLoading && (
                    <div style={styles.meta}>Checking for existing athletes…</div>
                  )}
                  {matches.length > 0 && (
                    <div style={styles.matchBox}>
                      <div style={styles.matchTitle}>
                        {hasExactMatch
                          ? exactMatchInClub
                            ? `${
                                hostClubName || 'Your club'
                              } member — select them below to add them to this meet.`
                            : 'This athlete is already on SplitFast under this contact email — select them below to add them to this meet.'
                          : `${matches.length} possible match${
                              matches.length === 1 ? '' : 'es'
                            } — this athlete may already exist. Pick a match below instead of creating a new athlete:`}
                      </div>
                      <div style={styles.matchList}>
                        {matches.map((m) => {
                          const tier = MATCH_TIER[m.matchTier] || {
                          label: 'Possible match',
                          color: '#6b7280',
                        };
                        return (
                          <div key={m.athleteProfileID} style={styles.matchRow}>
                            <div style={{ minWidth: 0 }}>
                              <div style={styles.matchName}>
                                {m.firstName} {m.lastName} · {m.gender}
                                {m.ageGroup
                                  ? ` · ${formatAgeGroup(m.ageGroup)}`
                                  : ''}
                              </div>
                              <div style={styles.matchMeta}>
                                DOB: {formatDob(m.birthDate)}
                                {m.isInThisClub ? ' · In your club' : ' · Not in your club'}
                                {' · '}
                                <span style={{ color: tier.color, fontWeight: 600 }}>
                                  {tier.label}
                                </span>
                              </div>
                              {m.primaryContactName || m.primaryContactEmail ? (
                                <div style={styles.matchMeta}>
                                  Contact: {m.primaryContactName}
                                  {m.primaryContactEmail
                                    ? ` · ${m.primaryContactEmail}`
                                    : ''}
                                </div>
                              ) : null}
                            </div>
                            <button
                              style={
                                m.isInThisClub
                                  ? styles.registerButton
                                  : styles.useButton
                              }
                              onClick={() => chooseMatch(m)}
                            >
                              {m.isInThisClub ? 'Register' : 'Add as visitor'}
                            </button>
                          </div>
                        );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}

              {clubFieldShown && (
                <div style={styles.bibClubGrid}>
                  <Field label="Bib number">
                    {registerAsIs ? (
                      // In-club athlete keeps their existing bib — shown read-only so the admin can confirm it.
                      <input
                        style={{ ...styles.input, ...styles.inputReadonly }}
                        value={linked.inClubAthleteNumber || ''}
                        readOnly
                      />
                    ) : (
                      <input
                        type="number"
                        min="1"
                        style={styles.input}
                        value={clubNumber}
                        onChange={(e) => {
                          setClubNumber(e.target.value);
                          setNumberHint('');
                        }}
                        placeholder="Required"
                      />
                    )}
                    {numberHint ? (
                      <span style={styles.fieldHint}>{numberHint}</span>
                    ) : null}
                  </Field>
                  <Field
                    label="Representing club"
                    style={{ gridColumn: 'span 2' }}
                  >
                    <CreatableClubSelect
                      clubs={clubsList}
                      value={lockedToHostClub ? hostClubName : externalClub}
                      disabled={lockedToHostClub}
                      onChange={handleClubChange}
                    />
                    {lockedToHostClub ? (
                      <span style={styles.fieldHint}>
                        Club members represent {hostClubName}.
                      </span>
                    ) : null}
                  </Field>
                </div>
              )}

              {showEvents && renderEvents()}
            </div>

            <div style={styles.footer}>
              <button
                onClick={handleSave}
                disabled={isSaving || eventsLoading || !canSave}
                style={{
                  ...styles.saveButton,
                  ...(isSaving || eventsLoading || !canSave
                    ? styles.saveButtonDisabled
                    : {}),
                }}
              >
                {isSaving
                  ? 'Saving…'
                  : mode === 'edit'
                  ? 'Save'
                  : registerAsIs
                  ? 'Register'
                  : 'Register Athlete'}
              </button>
            </div>

            <div style={styles.closeBtn} onClick={handleClose}>
              <X size={24} />
            </div>
          </div>
        </div>
      </div>

      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={() => setShowPrompt(false)}
        title="Discard changes?"
        description="You have unsaved changes. Leave without saving?"
        confirmButtonText="Discard"
        confirmButtonVariant="danger"
        onConfirm={() => {
          setShowPrompt(false);
          onClose(false);
        }}
        cancelButtonText="Keep editing"
        onCancel={() => setShowPrompt(false)}
      />
    </>
  );
};

const Field = ({ label, children, style }) => (
  <div style={{ ...styles.field, ...style }}>
    <label style={styles.fieldLabel}>{label}</label>
    {children}
  </div>
);

const styles = {
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 8,
    width: '100%',
    maxWidth: 720,
    // Fixed height so the dialog never resizes/re-centres as the admin types (matches stream in, contact fields
    // appear/hide). Everything inside scrolls instead — see modalContent / form / matchList.
    height: 'min(90vh, 720px)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  modalContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '36px 0 0',
  },
  modalTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: '#cf2c47',
    textAlign: 'center',
    padding: '0 40px',
  },
  subtitle: {
    textAlign: 'center',
    color: '#6b7280',
    fontSize: 13.5,
    margin: '6px 0 0',
    padding: '0 40px',
  },
  form: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '0 40px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    marginTop: 24,
  },
  summaryCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: 12,
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
  },
  linkedHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  linkClear: {
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  linkNote: { fontSize: 12.5, color: '#7c3aed' },
  inClubNote: { fontSize: 12.5, color: '#047857' },
  backLink: {
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    color: '#2563eb',
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'underline',
    padding: 0,
  },
  addNewButton: {
    padding: '10px',
    border: '1px dashed #cbd5e1',
    borderRadius: 6,
    background: '#f8fafc',
    color: '#2563eb',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  alreadyBadge: {
    flexShrink: 0,
    fontSize: 12.5,
    fontWeight: 600,
    color: '#6b7280',
    background: '#f3f4f6',
    padding: '5px 10px',
    borderRadius: 4,
  },
  meta: { fontSize: 13, color: '#6b7280' },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: 12,
  },
  // Bib + representing club row: 3 equal columns so the bib lines up under "Contact first name" (1/3) and the club
  // takes the remaining 2/3 under the last-name + email columns.
  bibClubGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 12,
  },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: 13, fontWeight: 600, color: '#374151' },
  fieldHint: { fontSize: 12, color: '#2563eb' },
  dobWrap: { position: 'relative' },
  dobEchoSide: {
    position: 'absolute',
    left: 'calc(100% + 10px)',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 13,
    color: '#2563eb',
    whiteSpace: 'nowrap',
  },
  input: {
    padding: '8px 10px',
    border: '1px solid #d1d5db',
    borderRadius: 4,
    fontSize: 14,
    background: '#fff',
  },
  inputReadonly: { background: '#f3f4f6', color: '#6b7280', cursor: 'default' },
  matchBox: {
    border: '1px solid #fcd34d',
    background: '#fffbeb',
    borderRadius: 8,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  matchTitle: { fontSize: 13, fontWeight: 600, color: '#92400e' },
  // Caps the list to ~5 rows; the rest scroll, so the match box (and the dialog) keep a steady height.
  matchList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    maxHeight: 300,
    overflowY: 'auto',
    paddingRight: 2,
  },
  matchRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    padding: '6px 8px',
    background: '#fff',
    border: '1px solid #fde68a',
    borderRadius: 6,
  },
  matchName: { fontWeight: 600, color: '#111827', fontSize: 14 },
  matchMeta: { fontSize: 12.5, color: '#6b7280' },
  useButton: {
    flexShrink: 0,
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12.5,
    cursor: 'pointer',
  },
  registerButton: {
    flexShrink: 0,
    background: '#27ae60',
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '5px 10px',
    fontSize: 12.5,
    cursor: 'pointer',
  },
  eventsSection: { display: 'flex', flexDirection: 'column', gap: 10 },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionLabel: { fontSize: 15, fontWeight: 600, color: '#333' },
  sectionLabelMeta: { fontWeight: 400, color: '#9ca3af' },
  eventCount: {
    fontSize: 13,
    fontWeight: 600,
    color: '#cf2c47',
    background: '#fce4e8',
    padding: '4px 10px',
    borderRadius: 12,
  },
  eventsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: 8,
    maxHeight: 200,
    overflowY: 'auto',
    padding: 4,
    border: '2px solid #e2e2e2',
    borderRadius: 6,
    background: '#fafafa',
  },
  eventItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 12px',
    background: '#fff',
    border: '2px solid #e2e2e2',
    borderRadius: 4,
    cursor: 'pointer',
    userSelect: 'none',
  },
  checkbox: {
    width: 18,
    height: 18,
    cursor: 'pointer',
    accentColor: '#cf2c47',
    flexShrink: 0,
    margin: 0,
  },
  eventName: { fontSize: 14, fontWeight: 500, color: '#333' },
  maxNote: { fontSize: 13, color: '#374151', fontWeight: 500, marginTop: -2 },
  maxNoteReached: {
    fontSize: 13,
    color: '#b45309',
    fontWeight: 600,
    marginTop: -2,
  },
  emptyBox: { padding: 16, color: '#6b7280', fontSize: 14, textAlign: 'center' },
  footer: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 40px',
    borderTop: '1px solid #eef0f2',
    background: '#fff',
  },
  saveButton: {
    backgroundColor: '#cf2c47',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '12px 48px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
  },
  saveButtonDisabled: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  closeBtn: {
    position: 'absolute',
    top: 20,
    right: 20,
    cursor: 'pointer',
    color: '#6b7280',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default AthleteEventsEditPopup;
